import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Layout } from '../../components/Layout';
import { clientsApi, CreateClientRequest } from '../../services/clients';
import { Client, ClientStatus } from '../../types';

export const ClientForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEdit = Boolean(id);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState<CreateClientRequest>({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    status: 'prospect',
    notes: '',
    address: { street: '', city: '', state: '', postalCode: '' },
  });

  useEffect(() => {
    if (isEdit && id) {
      loadClient(id);
    }
  }, [id, isEdit]);

  const loadClient = async (clientId: string) => {
    try {
      setLoading(true);
      const client = await clientsApi.get(clientId);
      setForm({
        firstName: client.firstName,
        lastName: client.lastName,
        email: client.email || '',
        phone: client.phone || '',
        status: client.status,
        notes: client.notes || '',
        address: client.address || { street: '', city: '', state: '', postalCode: '' },
      });
    } catch (err) {
      setError('Failed to load client');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.firstName.trim() || !form.lastName.trim()) {
      setError('First name and last name are required');
      return;
    }

    try {
      setSaving(true);
      setError('');
      if (isEdit && id) {
        await clientsApi.update(id, form);
      } else {
        await clientsApi.create(form);
      }
      navigate('/clients');
    } catch (err) {
      setError('Failed to save client');
    } finally {
      setSaving(false);
    }
  };

  const inputStyle = {
    width: '100%',
    padding: '10px',
    border: '1px solid #ddd',
    borderRadius: '4px',
    fontSize: '14px',
  };

  const labelStyle = {
    display: 'block',
    marginBottom: '4px',
    fontWeight: 500 as const,
    color: '#333',
  };

  if (loading) {
    return <Layout><div style={{ padding: '24px' }}>Loading...</div></Layout>;
  }

  return (
    <Layout>
      <div style={{ padding: '24px', maxWidth: '600px' }}>
        <h1>{isEdit ? 'Edit Client' : 'New Client'}</h1>

        {error && <div style={{ color: 'red', marginBottom: '16px' }}>{error}</div>}

        <form onSubmit={handleSubmit}>
          <div style={{ display: 'grid', gap: '16px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              <div>
                <label style={labelStyle}>First Name *</label>
                <input
                  style={inputStyle}
                  value={form.firstName}
                  onChange={e => setForm({ ...form, firstName: e.target.value })}
                  required
                />
              </div>
              <div>
                <label style={labelStyle}>Last Name *</label>
                <input
                  style={inputStyle}
                  value={form.lastName}
                  onChange={e => setForm({ ...form, lastName: e.target.value })}
                  required
                />
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              <div>
                <label style={labelStyle}>Email</label>
                <input
                  style={inputStyle}
                  type="email"
                  value={form.email}
                  onChange={e => setForm({ ...form, email: e.target.value })}
                />
              </div>
              <div>
                <label style={labelStyle}>Phone</label>
                <input
                  style={inputStyle}
                  value={form.phone}
                  onChange={e => setForm({ ...form, phone: e.target.value })}
                />
              </div>
            </div>

            <div>
              <label style={labelStyle}>Status</label>
              <select
                style={inputStyle}
                value={form.status}
                onChange={e => setForm({ ...form, status: e.target.value as ClientStatus })}
              >
                <option value="prospect">Prospect</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="archived">Archived</option>
              </select>
            </div>

            <div>
              <label style={labelStyle}>Street Address</label>
              <input
                style={inputStyle}
                value={form.address?.street || ''}
                onChange={e => setForm({ ...form, address: { ...form.address, street: e.target.value } })}
              />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: '16px' }}>
              <div>
                <label style={labelStyle}>City</label>
                <input
                  style={inputStyle}
                  value={form.address?.city || ''}
                  onChange={e => setForm({ ...form, address: { ...form.address, city: e.target.value } })}
                />
              </div>
              <div>
                <label style={labelStyle}>State</label>
                <input
                  style={inputStyle}
                  value={form.address?.state || ''}
                  onChange={e => setForm({ ...form, address: { ...form.address, state: e.target.value } })}
                />
              </div>
              <div>
                <label style={labelStyle}>ZIP</label>
                <input
                  style={inputStyle}
                  value={form.address?.postalCode || ''}
                  onChange={e => setForm({ ...form, address: { ...form.address, postalCode: e.target.value } })}
                />
              </div>
            </div>

            <div>
              <label style={labelStyle}>Notes</label>
              <textarea
                style={{ ...inputStyle, minHeight: '100px', resize: 'vertical' }}
                value={form.notes}
                onChange={e => setForm({ ...form, notes: e.target.value })}
              />
            </div>

            <div style={{ display: 'flex', gap: '12px', marginTop: '16px' }}>
              <button
                type="submit"
                disabled={saving}
                style={{
                  backgroundColor: '#1976d2',
                  color: 'white',
                  border: 'none',
                  padding: '12px 24px',
                  borderRadius: '4px',
                  cursor: saving ? 'not-allowed' : 'pointer',
                  opacity: saving ? 0.7 : 1,
                }}
              >
                {saving ? 'Saving...' : (isEdit ? 'Update Client' : 'Create Client')}
              </button>
              <button
                type="button"
                onClick={() => navigate('/clients')}
                style={{
                  backgroundColor: '#f5f5f5',
                  color: '#333',
                  border: '1px solid #ddd',
                  padding: '12px 24px',
                  borderRadius: '4px',
                  cursor: 'pointer',
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </form>
      </div>
    </Layout>
  );
};
